
import { debounce, isEqual } from 'lodash'
import { mapActions, mapState, mapMutations } from 'vuex'
import filtersJson from './filters.json'
import { isMobile, isTablet } from 'mobile-device-detect'
import togglePageScroll from '../../../mixins/toggle-page-scroll'
import NrDatepicker from '@nextrequestco/components/src/components/NrDatePicker/nr-datepicker.vue'
import NrIcon from '@nextrequestco/components/src/components/NrIcon/NrIcon.vue'
import NrModal from '@nextrequestco/components/src/components/NrModal/nr-modal.vue'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip/NrTooltip.vue'
import NrButton from '@nextrequestco/components/src/components/NrButton/nr-button.vue'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton/nr-icon-button.vue'
import NrSelect from '@nextrequestco/components/src/components/NrSelect/nr-select.vue'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput/nr-form-input.vue'
import NrFormLabel from '@nextrequestco/components/src/components/form-elements/nr-form-label.vue'
import {
  QUERY_FILTER_IDS_TO_OPTION_KEY_MAP,
  filterState
} from '../../../store/filters-state'

const __default__ = {
  components: {
    NrDatepicker,
    NrIcon,
    NrModal,
    NrTooltip,
    NrButton,
    NrSelect,
    NrFormInput,
    NrIconButton,
    NrFormLabel
  },
  mixins: [togglePageScroll],
  props: {
    notGuest: { type: Boolean, default: true },
    label: { type: String, default: 'Save Views' },
    isDisabled: { type: Boolean, default: false },
    currentUser: { type: Object },
    previewUserId: { type: [String, Number] },
    account: { type: Object, default: () => ({}) },
    filtersHeight: { type: String, default: '300' },
    heroOpen: { type: Boolean },
    sidebarHasMinWidth: { type: Boolean }
  },
  data() {
    return {
      isDeleteModalActive: false,
      height: 300,
      device: null,
      isComponentModalActive: false,
      saveViewName: '',
      filtersJson,
      autoGenerateData: filtersJson.autoGenerateData
    }
  },
  mounted() {
    this.height = this.filtersHeight

    if (isMobile) {
      this.device = 'isMobile'
    } else if (isTablet) {
      this.device = 'isTable'
    }
    this.togglePageScroll()
  },
  watch: {
    filtersHeight() {
      this.height = this.filtersHeight
    }
  },
  computed: {
    ...mapState('requests', [
      'savedViews',
      'selectedSavedView',
      'filters',
      'filterOptions',
      'filterFetchState',
      'requests',
      'height'
    ]),

    allFiltersDeselected() {
      return isEqual(this.filters, filterState)
    },

    autoGenerateDataFilter() {
      return Object.keys(this.filtersJson.autoGenerateData)
        .map((filterKey) => {
          return this.filtersJson.autoGenerateData[filterKey]
        })
        .filter((filter) => this.canShowFilterField(filter))
    },

    myId() {
      return this.previewUserId || this.currentUser.id
    },

    asideMaxHeight() {
      return this.height + 'px'
    }
  },
  methods: {
    ...mapActions('requests', [
      'fetchFilterOptions',
      'setAddSavedViews',
      'setDeleteSelectedView',
      'fetchRequests',
      'applyFilters',
      'configureRequestsTableColumns',
      'fetchOptionsFromFilterState'
    ]),

    ...mapMutations('requests', [
      'setSelectedSavedView',
      'setFilterOptions',
      'resetFilters'
    ]),

    filteredFields(checkboxes) {
      return checkboxes.filter((item) => this.canShowFilterField(item))
    },

    filterOptionValues(filterName) {
      const arry = this.filters[filterName]
        .map((id) => {
          return this.filterOptions[
            QUERY_FILTER_IDS_TO_OPTION_KEY_MAP[filterName]
          ].find((option) => option.id === id)
        })
        .filter((option) => !!option)
      return this.parseOptionsForNrSelect(arry, 'name_with_email')
    },

    async setFilters(filters) {
      this.setSelectedSavedView(null)
      this.$cookies.remove('savedView')
      await this.$store.dispatch('requests/setFilters', filters)
    },

    isMyRequestChecked(filterName) {
      if (!this.currentUser) return false
      return this.filters[filterName].includes(this.myId)
    },

    formatMyRequestsFilter(filter, enable) {
      // NOTE: Non-signed in users cannot see this UI
      if (!this.currentUser) return

      if (filter !== 'requester_ids') {
        this.setFilterOptions({
          [QUERY_FILTER_IDS_TO_OPTION_KEY_MAP[filter]]: [
            ...this.filterOptions[QUERY_FILTER_IDS_TO_OPTION_KEY_MAP[filter]],
            this.currentUser
          ]
        })
      }

      if (enable) {
        this.setFilters({
          [filter]: [...this.filters[filter], this.myId]
        })
      } else {
        this.setFilters({
          [filter]: this.filters[filter].filter(
            (id) => Number(id) !== Number(this.myId)
          )
        })
      }
    },

    async handleEmitSaveView() {
      this.isComponentModalActive = false
      try {
        await this.setAddSavedViews(this.saveViewName)
        this.saveViewName = ''
        this.$cookies.set('savedView', this.savedViews[0].id)
        this.$toasted.show('Saved view has been added!', {
          type: 'success',
          duration: 1000
        })
        this.fetchRequests().finally(() => {
          if (this.requests.length) {
            this.configureRequestsTableColumns(this.$ability)
          }
          this.togglePageScroll()
        })
      } catch (error) {
        this.$toasted.error('Saved view was not able to be saved!', {
          duration: 2000
        })
      }
    },

    async handleSavedView(option) {
      this.resetFilters()

      if (option) {
        await this.setFilters(option.filters)
        this.$cookies.set('savedView', option.id)
      } else {
        this.$cookies.remove('savedView')
      }

      // Call apply filters.
      this.applyFilters()
      this.setSelectedSavedView(option)
      this.fetchOptionsFromFilterState()
    },

    handleDeleteSavedView() {
      const savedViewId = this.selectedSavedView.id
      if (!savedViewId) return
      this.setDeleteSelectedView(savedViewId)
      this.isDeleteModalActive = false
    },

    handleFilterTyping: debounce(async function (name = '', filter) {
      if (!filter) return
      this.fetchFilterOptions({ filterName: filter, search_text: name })
    }, 500),

    handleFilterScroll: debounce(function (filter) {
      this.handleFilterTyping(this.filterFetchState[filter].search_text, filter)
    }, 250),

    canShowFilterLabelForGuest(filter) {
      // Don't show the 'Request visibility' label if you are a guest or not signed in
      return !(
        filter.name === 'Request visibility' &&
        !this.$ability.can('read', 'staff_only_items')
      )
    },

    canShowFilterField(filter) {
      // Show the filter field if you are staff OR if 'filter.showGuest' is
      // true and if you are a guest or not signed in
      const accountSetting = {
        embargoed: this.account.embargoing_available,
        department_published: this.account.restricted_visibilities_feature_flag
      }
      if (['embargoed', 'department_published'].includes(filter.field)) {
        return (
          (this.$ability.can('read', 'staff_only_items') ||
            (filter.showGuest &&
              !this.$ability.can('read', 'staff_only_items'))) &&
          this.account &&
          accountSetting[filter.field]
        )
      }
      return (
        this.$ability.can('read', 'staff_only_items') ||
        (filter.showGuest && !this.$ability.can('read', 'staff_only_items'))
      )
    },

    parseOptionsForNrSelect(options, keyName): [] {
      if (!Array.isArray(options)) {
        options = [options]
      }

      return options
        .filter(
          (option) => option && (option[keyName] || option.label || option.name)
        )
        .map((option) => {
          return {
            value: option.id,
            label: option[keyName] || option.label || option.name
          }
        })
    },

    handleAutogeneratedDataSelect(selected, filter) {
      this.setFilters({ [filter]: selected.map((item) => item.value) })
    }
  }
}

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "623d1fda": (_ctx.asideMaxHeight)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__